import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { PortalAuthProvider, usePortalAuth } from "./hooks/usePortalAuth";
import { UIVersionProvider } from "./contexts/UIVersionContext";
import { phPageview } from "./lib/posthog";
import { LandingPage } from "./pages/LandingPage";
import { DashboardPage } from "./pages/DashboardPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { CreditNotesPage } from "./pages/CreditNotesPage";
import { DeliveryNotesPage } from "./pages/DeliveryNotesPage";
import { ContractsPage } from "./pages/ContractsPage";
import { PricesPage } from "./pages/PricesPage";
import { OrdersPage } from "./pages/OrdersPage";
import MessagesPage from "./pages/MessagesPage";
import { ProductSheetsPage } from "./pages/ProductSheetsPage";
import { TraceabilityPage } from "./pages/TraceabilityPage";
import { ProfilePage } from "./pages/ProfilePage";
import { PortalShell } from "./components/layout/PortalShell";
import type { ReactElement } from "react";

/**
 * LOT P4 — $pageview PostHog sur changement de route. `capture_pageview: true` ne
 * couvre que le chargement initial (pas la navigation SPA) ; phPageview() ignore
 * volontairement son tout premier appel pour ne pas doubler celui-là.
 * No-op complet quand le traçage est désactivé (dev local / kill switch).
 * Doit vivre SOUS <BrowserRouter> pour que useLocation() soit disponible.
 */
function PosthogRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    phPageview();
  }, [location.pathname]);
  return null;
}

function RequireAuth({ children }: { children: ReactElement }) {
  const { isAuthenticated } = usePortalAuth();
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return children;
}

export function AppRouter() {
  return (
    <div data-testid="portal-root" className="min-h-screen bg-cream text-ink">
      <PosthogRouteTracker />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route element={<RequireAuth><PortalShell /></RequireAuth>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/credit-notes" element={<CreditNotesPage />} />
          <Route path="/delivery-notes" element={<DeliveryNotesPage />} />
          <Route path="/contracts" element={<ContractsPage />} />
          <Route path="/prices" element={<PricesPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/product-sheets" element={<ProductSheetsPage />} />
          <Route path="/traceability" element={<TraceabilityPage />} />
          <Route path="/profile" element={<ProfilePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export function App() {
  return (
    <PortalAuthProvider>
      <UIVersionProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </UIVersionProvider>
    </PortalAuthProvider>
  );
}
